import { AlertCircle, FileText, Link, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import GoogleDriveIcon from './GoogleDriveIcon';
import { completeContextUpload, completeDriveFolderContext, createContextUpload, failDriveFolderContext, fetchContextSources, startDriveFolderContext, updateDriveFolderProgress } from '../lib/api';
import { getGoogleDriveFolderFiles, pickGoogleDriveFolder, requestGoogleDriveAccess } from '../lib/googleDrive';
import { useAuth } from '../lib/auth';
import { trackJourney } from '../lib/journey';
import type { ContextSource } from '../types';

type Props = {
  projectId?: string;
  initialIntent?: string | null;
  initialSources?: ContextSource[];
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  onAttachmentStarted?: () => void;
  onPendingChange?: (pending: boolean) => void;
  onInsertText: (text: string) => void;
  assetsContainer?: HTMLElement | null;
  readOnly?: boolean;
};

const acceptedFiles = '.pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown';
type PendingFileContext = { id: string; contextId?: string; name: string; description: string; status: 'processing' | 'failed' };
type DriveProgress = {
  stage: 'reading' | 'processing' | 'finalizing';
  currentFileName?: string;
  currentFileNumber?: number;
  totalFiles: number;
};

function driveProgressDescription(source: ContextSource, progress?: DriveProgress) {
  if (progress?.stage === 'finalizing') return `Finalizing ${progress.totalFiles} files...`;
  if (progress?.currentFileNumber && progress.currentFileName) {
    const action = progress.stage === 'reading' ? 'Reading' : 'Processing';
    return `${action} file ${progress.currentFileNumber} of ${progress.totalFiles} · ${progress.currentFileName}`;
  }
  const totalFiles = source.metadata?.fileCount || 0;
  const processedFiles = source.metadata?.contextIds?.length || 0;
  if (totalFiles && processedFiles >= totalFiles) return `Finalizing ${totalFiles} files...`;
  if (totalFiles && processedFiles) return `Processed ${processedFiles} of ${totalFiles} files...`;
  if (totalFiles) return `Processing 1 of ${totalFiles} files...`;
  return 'Scanning folder...';
}

function formatMegabytes(bytes: unknown) {
  const megabytes = Math.max(0, Number(bytes) || 0) / (1024 * 1024);
  if (megabytes > 0 && megabytes < 0.01) return '<0.01 MB';
  return `${Number(megabytes.toFixed(megabytes >= 1 ? 1 : 2))} MB`;
}

function SourceIcon({ source }: { source: ContextSource }) {
  if (source.metadata?.provider === 'google_drive') return <GoogleDriveIcon size={18} />;
  if (source.sourceType === 'file') return <FileText size={17} />;
  return <Link size={17} />;
}

export default function ContextLibrary({ projectId, initialIntent, initialSources = [], selectedIds, onSelectedIdsChange, onAttachmentStarted, onPendingChange, onInsertText, assetsContainer, readOnly = false }: Props) {
  const { getToken } = useAuth();
  const addWrapRef = useRef<HTMLDivElement>(null);
  const driveJobsRef = useRef(new Set<string>());
  const uploadsInFlightRef = useRef(new Set<string>());
  const resumeDriveRef = useRef<(source: ContextSource, accessToken: string) => void>(() => undefined);
  const [sources, setSources] = useState<ContextSource[]>(initialSources);
  const [menuOpen, setMenuOpen] = useState(initialIntent === 'file' || initialIntent === 'drive');
  const [pendingFiles, setPendingFiles] = useState<PendingFileContext[]>([]);
  const [driveProgress, setDriveProgress] = useState<Record<string, DriveProgress>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    const token = await getToken();
    const loaded = (await fetchContextSources(token)).contextSources;
    setSources((current) => [...loaded, ...current.filter((source) => !loaded.some((item) => item.id === source.id))]);
  }, [getToken]);

  useEffect(() => { void refresh().catch(() => setError('Could not load saved context.')); }, [refresh]);

  useEffect(() => {
    if (!initialSources.length) return;
    setSources((current) => [...current.filter((source) => !initialSources.some((initial) => initial.id === source.id)), ...initialSources]);
  }, [initialSources]);

  useEffect(() => {
    if (!sources.some((source) => source.status === 'pending' || source.status === 'processing')) return;
    const resumePendingUploads = async () => {
      const token = await getToken();
      for (const source of sources) {
        if (!selectedIds.includes(source.id) || source.sourceType !== 'file' || source.status !== 'pending') continue;
        if (uploadsInFlightRef.current.has(source.id)) continue;
        try {
          const completed = await completeContextUpload(token, source.id);
          replaceSource(completed.contextSource);
        } catch {
          // The object may still be uploading. The next poll retries completion.
        }
      }
    };
    void resumePendingUploads();
    const timer = window.setInterval(() => { void refresh(); void resumePendingUploads(); }, 5000);
    return () => window.clearInterval(timer);
  }, [getToken, refresh, selectedIds, sources]);

  const persistedPending = selectedIds.some((id) => sources.some((source) => source.id === id && source.status !== 'ready'));
  useEffect(() => onPendingChange?.(busy || persistedPending), [busy, onPendingChange, persistedPending]);

  useEffect(() => {
    for (const source of sources) {
      if (!selectedIds.includes(source.id) || source.status !== 'processing' || source.metadata?.provider !== 'google_drive') continue;
      const accessToken = window.sessionStorage.getItem(`maxed:drive:${source.id}`);
      if (accessToken) resumeDriveRef.current(source, accessToken);
    }
  }, [selectedIds, sources]);

  useEffect(() => {
    if (!menuOpen) return;
    function closeMenu(event: PointerEvent) {
      if (!addWrapRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('pointerdown', closeMenu);
    return () => document.removeEventListener('pointerdown', closeMenu);
  }, [menuOpen]);

  function canAttach(count = 1) {
    if (selectedIds.length + count <= 5) return true;
    setError('You can include up to five context sources.');
    return false;
  }

  function attachSource(source: ContextSource) {
    setSources((current) => [...current.filter((item) => item.id !== source.id), source]);
    if (!selectedIds.includes(source.id)) {
      onSelectedIdsChange([...selectedIds, source.id]);
      void trackJourney(getToken, 'context_selected', { properties: { sourceType: source.sourceType } });
    }
  }

  function replaceSource(source: ContextSource) {
    setSources((current) => [...current.filter((item) => item.id !== source.id), source]);
  }

  async function uploadFile(file: File, token?: string, attach = true, onStatus?: (description: string) => void, onCreated?: (source: ContextSource) => void, onUploaded?: () => void) {
    const authToken = token || await getToken();
    const created = await createContextUpload(authToken, file, attach ? projectId : undefined);
    uploadsInFlightRef.current.add(created.contextSource.id);
    try {
      if (attach) attachSource(created.contextSource);
      onCreated?.(created.contextSource);
      const uploadResponse = await fetch(created.upload.url, {
        method: created.upload.method,
        headers: created.upload.headers,
        body: file,
      });
      if (!uploadResponse.ok) throw new Error('File upload failed.');
      onUploaded?.();
      onStatus?.('Starting document processing...');
      const completed = await completeContextUpload(authToken, created.contextSource.id);
      if (attach) replaceSource(completed.contextSource);
      if (attach) void trackJourney(getToken, 'context_created', { properties: { sourceType: 'file' } });
      return completed.contextSource;
    } finally {
      uploadsInFlightRef.current.delete(created.contextSource.id);
    }
  }

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    setMenuOpen(false);
    if (!file || busy || !canAttach()) return;
    onAttachmentStarted?.();
    const pendingId = crypto.randomUUID();
    let contextId = '';
    let uploaded = false;
    setPendingFiles((current) => [...current, { id: pendingId, name: file.name, description: 'Uploading document...', status: 'processing' }]);
    setBusy(true); setError('');
    try {
      await uploadFile(file, undefined, true, (description) => {
        setPendingFiles((current) => current.map((item) => item.id === pendingId ? { ...item, description } : item));
      }, (source) => {
        contextId = source.id;
        setPendingFiles((current) => current.map((item) => item.id === pendingId ? { ...item, contextId: source.id } : item));
      }, () => { uploaded = true; });
      setPendingFiles((current) => current.filter((item) => item.id !== pendingId));
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'File could not be uploaded.';
      if (contextId && !uploaded) onSelectedIdsChange(selectedIds.filter((id) => id !== contextId));
      setPendingFiles((current) => current.map((item) => item.id === pendingId ? { ...item, description: message, status: 'failed' } : item));
      setError(message);
    } finally { setBusy(false); }
  }

  async function addDriveFolder() {
    if (busy || !canAttach() || !projectId) return;
    setMenuOpen(false); setBusy(true); setError('');
    try {
      const accessToken = await requestGoogleDriveAccess();
      const folder = await pickGoogleDriveFolder(accessToken);
      onAttachmentStarted?.();
      const token = await getToken();
      const { contextSource } = await startDriveFolderContext(token, projectId, folder);
      window.sessionStorage.setItem(`maxed:drive:${contextSource.id}`, accessToken);
      attachSource(contextSource);
      runDriveFolder(contextSource, accessToken);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Google Drive folder could not be added.';
      if (!/cancelled/i.test(message)) setError(message);
    } finally { setBusy(false); }
  }

  function runDriveFolder(source: ContextSource, accessToken: string) {
    if (driveJobsRef.current.has(source.id)) return;
    driveJobsRef.current.add(source.id);
    setBusy(true); setError('');
    void (async () => {
      try {
        const folderId = source.metadata?.folderId;
        const folderName = source.metadata?.folderName;
        if (!folderId || !folderName) throw new Error('Google Drive folder details are missing.');
        const imported = await getGoogleDriveFolderFiles(accessToken, folderId, source.metadata?.resourceKey || undefined, (progress) => {
          setDriveProgress((current) => ({
            ...current,
            [source.id]: { stage: 'reading', ...progress },
          }));
        });
        const token = await getToken();
        const sourceIds = [...(source.metadata?.contextIds || [])];
        for (const file of imported.files.slice(sourceIds.length)) {
          setDriveProgress((current) => ({
            ...current,
            [source.id]: {
              stage: 'processing',
              currentFileName: file.name,
              currentFileNumber: sourceIds.length + 1,
              totalFiles: imported.files.length,
            },
          }));
          sourceIds.push((await uploadFile(file, token, false)).id);
          const updated = await updateDriveFolderProgress(token, source.id, {
            folderName,
            contextIds: sourceIds,
            fileCount: imported.files.length,
            downloadedBytes: imported.totalBytes,
            skippedCount: imported.skippedCount,
            scanTruncated: imported.scanTruncated,
          });
          replaceSource(updated.contextSource);
        }
        setDriveProgress((current) => ({
          ...current,
          [source.id]: { stage: 'finalizing', totalFiles: imported.files.length },
        }));
        await waitForReadySources(token, sourceIds);
        const completed = await completeDriveFolderContext(token, source.id, folderName, sourceIds);
        replaceSource(completed.contextSource);
        window.sessionStorage.removeItem(`maxed:drive:${source.id}`);
        void trackJourney(getToken, 'context_created', { properties: { sourceType: 'google_drive' } });
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : 'Google Drive folder could not be added.';
        setError(message);
        try {
          const token = await getToken();
          const failed = await failDriveFolderContext(token, source.id, message);
          replaceSource(failed.contextSource);
          window.sessionStorage.removeItem(`maxed:drive:${source.id}`);
        } catch {
          // A reload can interrupt this request; the persisted job will resume in the new page.
        }
      } finally {
        driveJobsRef.current.delete(source.id);
        setDriveProgress((current) => {
          const next = { ...current };
          delete next[source.id];
          return next;
        });
        setBusy(false);
      }
    })();
  }

  resumeDriveRef.current = runDriveFolder;

  async function waitForReadySources(token: string | undefined, ids: string[]) {
    const deadline = Date.now() + 3 * 60 * 1000;
    while (Date.now() < deadline) {
      const loaded = (await fetchContextSources(token)).contextSources;
      const byId = new Map(loaded.map((source) => [source.id, source]));
      const pending = ids.map((id) => byId.get(id));
      if (pending.some((source) => source?.status === 'failed')) throw new Error('One or more Google Drive files could not be processed.');
      if (pending.every((source) => source?.status === 'ready')) return;
      await new Promise((resolve) => window.setTimeout(resolve, 5000));
    }
    throw new Error('Google Drive files are still processing. Please try again shortly.');
  }

  function remove(source: ContextSource) {
    window.sessionStorage.removeItem(`maxed:drive:${source.id}`);
    if (selectedIds.includes(source.id)) onSelectedIdsChange(selectedIds.filter((id) => id !== source.id));
  }

  const pendingSourceIds = new Set(pendingFiles.flatMap((file) => file.contextId ? [file.contextId] : []));
  const attachedSources = selectedIds
    .map((id) => sources.find((source) => source.id === id))
    .filter((source): source is ContextSource => Boolean(source))
    .filter((source) => !pendingSourceIds.has(source.id));

  const assets = (attachedSources.length > 0 || pendingFiles.length > 0) && <div className="context-assets">{pendingFiles.map((file) => (
        <div key={file.id} className="context-asset context-upload" title={file.name}>
          <span className="context-asset-icon"><FileText size={17} /></span>
          <span className="context-asset-copy"><strong>{file.name}</strong><small>{file.description}</small></span>
          {file.status === 'failed' && <button type="button" className="context-asset-state context-asset-remove failed" onClick={() => { setPendingFiles((current) => current.filter((item) => item.id !== file.id)); setError(''); }} aria-label={`Remove failed upload ${file.name}`}><X size={14} /></button>}
        </div>
      ))}{attachedSources.map((source) => (
        <div key={source.id} className={`context-asset${source.metadata?.provider === 'google_drive' ? ' context-drive-folder' : ''}`} title={source.metadata?.folderName || source.name}>
          <span className="context-asset-icon"><SourceIcon source={source} /></span>
          <span className="context-asset-copy"><strong>{source.metadata?.folderName || source.name}</strong>{source.metadata?.provider === 'google_drive' && source.status === 'ready' && <small>Free plan · {source.metadata.fileCount || 0} of 25 files<br />{formatMegabytes(source.metadata.downloadedBytes)} of 1 MB</small>}{source.status !== 'ready' && <small aria-live="polite">{source.status === 'failed' ? source.error || 'Error' : source.metadata?.provider === 'google_drive' ? driveProgressDescription(source, driveProgress[source.id]) : 'Processing...'}</small>}</span>
          {!readOnly && <button type="button" className={`context-asset-state context-asset-remove ${source.status}`} onClick={() => remove(source)} aria-label={source.metadata?.provider === 'google_drive' ? `Remove Google Drive folder ${source.metadata.folderName}` : `Remove context ${source.name}`}><X size={14} /></button>}
        </div>
      ))}</div>;

  return (
    <div className="context-attachments">
      {assetsContainer && assets ? createPortal(assets, assetsContainer) : assets}
      {!readOnly && <div className="context-attachment-footer">
        <div className="context-add-wrap" ref={addWrapRef}>
          <button type="button" className="context-add-button" onClick={() => setMenuOpen((open) => !open)} aria-label="Add meeting context" aria-expanded={menuOpen}><Plus size={19} /></button>
          {menuOpen && <div className="context-add-menu">
            <div className="context-file-option">
              <FileText size={17} />
              <span><strong>Document</strong><small>PDF, DOCX, TXT, or MD</small></span>
              <input className="context-file-input" type="file" accept={acceptedFiles} onChange={(event) => void upload(event)} disabled={busy} aria-label="Choose a document" />
            </div>
            <button type="button" onClick={() => { setMenuOpen(false); onInsertText('https://'); }}><Link size={17} /><span><strong>URL</strong><small>Insert a link in your prompt</small></span></button>
            <button type="button" onClick={() => void addDriveFolder()}><GoogleDriveIcon size={18} /><span><strong>Google Drive</strong><small>Choose any folder you can access</small></span></button>
          </div>}
        </div>
        <span>{busy ? 'Adding context...' : 'Add context'}</span>
      </div>}
      {readOnly && <div className="context-readonly-note">Shared assets are view-only</div>}
      {error && <p className="context-error"><AlertCircle size={14} /> {error}</p>}
    </div>
  );
}
